import React from 'react'
import qs from 'qs'
import clsx from 'clsx'
import { useAppSelector } from '../../../hooks/redux'
import { useHistory, useLocation } from 'react-router'
import { ArrowCircleDown } from 'iconsax-react'
import { AnimatePresence, motion } from 'framer-motion'
import { Badge, IconButton, useTheme } from '@mui/material'

import Header from './Header'
import Spacer from '../../atoms/Spacer'
import Controls from './Controls'
import Timeline from './Timeline'
import LoadingSpinner from '../../atoms/LoadingSpinner'
import TypingIndicator from '../../atoms/TypingIndicator'
import OfflineAlertBanner from '../../molecules/OfflineAlertBanner'

import CanSendProvider from '../../../providers/CanSendProvider'

import { useTaskAPI } from '../../../providers/TaskAPIProvider'
import { useTyping } from '../../../providers/TypingProvider'
import { useAgent } from '../../../providers/AgentProvider'
import { selectPresence } from '../../../slices/presence'
import { useUserAPI } from '../../../providers/UserAPIProvider'
import { useBreakpoint } from '../../../providers/BreakpointProvider'
import { useSocket } from '../../../providers/SocketProvider'
import { useQueueState } from '../../../providers/QueueStateProvider'
import { useVerification } from '../../../providers/VerificationProvider'
import { useChatScroll } from '../../../providers/ChatScrollProvider'
import { useTimelineTarget } from '@SonarCX/providers/TimelineTargetProvider'
import { selectAlert } from '../../../slices/alert'
import useDetectKeyboardOpen from '@SonarCX/hooks/useDetectKeyboardOpen'
import useStyles from './styles'

const ChatWindow = ({
  convo,
  onMessageSubmit,
  onCommentSubmit,
  onTypeSet,
  onTyping,
  closeSession = () => {},
  agents = [],
  canMention = false,
  setShowQueue,
  onMobile,
  isDirect,
  viewing,
  startRecording,
  stopRecording,
  recordingBlob,
  isRecording,
  recordingTime,
  clearRecord
}) => {
  const history = useHistory()
  const location = useLocation()
  const isKeyboardOpen = useDetectKeyboardOpen()

  // Gets props from ClientChats / PeerChat
  const { tasks } = useTaskAPI()
  const { getTyping } = useTyping()
  const { currentUser } = useAgent()
  const { consentVisible } = useAppSelector(selectAlert)
  const { presence } = useAppSelector(selectPresence)
  const { allUsers } = useUserAPI()
  const { isSmall, down1300 } = useBreakpoint()
  const { api } = useSocket()
  const {
    handleSelect: handleConvoSelect,
    id: convoID,
    accepted
  } = useQueueState()
  const { verified } = useVerification() ?? { verified: true }
  const {
    messageEndRef,
    targetRef,
    scrolled,
    setScrolled,
    scrollToBottom,
    quickScrollToBottom
  } = useChatScroll()
  const { targetID, setTargetID } = useTimelineTarget()

  const messageAreaRef = React.useRef()
  const chatContentAreaRef = React.useRef(null)

  const [search, setSearch] = React.useState('')
  const [loaded, setLoaded] = React.useState([])
  const [names, setNames] = React.useState([])
  const [filter, setFilter] = React.useState('')
  const [didInitialScroll, setDidInitialScroll] = React.useState(false)
  const [forceScroll, setForceScroll] = React.useState(true)
  const [hasNoNewUnread, setHasNoNewUnread] = React.useState(true)
  const [isAtBottom, setIsAtBottom] = React.useState(false)
  const [isNearlyAtBottom, setIsNearlyAtBottom] = React.useState(true)

  const classes = useStyles({
    smallMedia: onMobile,
    isDirect,
    down1300,
    viewing,
    isConsentVisible: consentVisible,
    isKeyboardOpen
  })

  const theme = useTheme()
  const { session, users = [], isNew } = convo
  const typingNames = React.useMemo(
    () => getTyping(session),
    [getTyping, session]
  )

  const handleScroll = element => {
    const isAtBottom =
      Math.abs(
        element.target.scrollHeight -
          element.target.scrollTop -
          element.target.clientHeight
      ) < 1
    const isNearlyAtBottom =
      Math.abs(
        element.target.scrollHeight -
          element.target.scrollTop -
          element.target.clientHeight
      ) < 100
    setIsAtBottom(isAtBottom)
    setIsNearlyAtBottom(isNearlyAtBottom)
  }

  React.useLayoutEffect(() => {
    if (isAtBottom) {
      setHasNoNewUnread(true)
      setForceScroll(true)
    }
  }, [isAtBottom])

  React.useEffect(() => {
    if (isAtBottom) {
      setHasNoNewUnread(true)
    }
  }, [isAtBottom])

  // Resets scroll flag when changing sessions or on new messages
  React.useEffect(() => {
    setDidInitialScroll(false)
  }, [session, convo.fetched, convoID])

  React.useLayoutEffect(() => {
    if (isNew && isAtBottom) {
      quickScrollToBottom()
    }
    if (isNew && !isAtBottom) {
      setHasNoNewUnread(false)
    }
  }, [isAtBottom, isNew, quickScrollToBottom])

  React.useEffect(() => {
    setForceScroll(true)
  }, [convoID])

  // Stop scrolling to target
  React.useEffect(() => {
    let mounted = true

    const onScroll = () => {
      setForceScroll(false)
      mounted && setTargetID(null)
      targetRef.current = null
    }

    const remove = () => document.removeEventListener('wheel', onScroll)

    document.addEventListener('wheel', onScroll, { passive: true })

    return () => {
      mounted = false
      remove()
    }
  }, [targetRef, setTargetID])

  React.useLayoutEffect(() => {
    if (forceScroll) {
      if (!isAtBottom) {
        quickScrollToBottom()
      }
      quickScrollToBottom()
    }
  })

  React.useEffect(() => {
    if (!messageAreaRef?.current) return

    messageAreaRef.current.addEventListener('scroll', handleScroll, {
      passive: true
    })

    return () => {
      if (!messageAreaRef?.current) return
      // eslint-disable-next-line react-hooks/exhaustive-deps
      messageAreaRef.current.removeEventListener('scroll', handleScroll)
    }
  }, [messageAreaRef])

  // Handles flow of notification
  React.useEffect(() => {
    if (!location || !history) {
      return
    }

    const params = qs.parse(location.search, { ignoreQueryPrefix: true })
    if (params.id) {
      // Triggers re-render and eventually finds the message to focus on
      setTargetID(params.id)

      // Stop this from happening again
      const newParams = { ...params }
      delete newParams.id
      const newQuery =
        Object.keys(newParams).length === 0
          ? ''
          : qs.stringify(newParams, {
              encode: false,
              addQueryPrefix: true
            })
      history.replace(`${location.pathname}${newQuery}`)
    }
  }, [location, history, setTargetID])

  // After rendering scroll to the latest message or a notification
  React.useEffect(() => {
    scrollToBottom()
  }, [search, filter, convoID, scrollToBottom])

  // Waits for tasks to be loaded before showing this timeline
  React.useEffect(() => {
    if (Object.values(tasks).every(task => Object.keys(task).length > 2)) {
      setLoaded(old => [...new Set([...old, session])])
    }
  }, [tasks, session])

  // Scroll if this timeline is loaded
  React.useEffect(() => {
    if (!loaded.includes(session) || didInitialScroll) {
      return
    }

    setDidInitialScroll(true)
    quickScrollToBottom()
  }, [loaded, session, quickScrollToBottom, didInitialScroll])

  React.useEffect(() => {
    if (!convo) {
      return
    }

    if (isDirect) {
      // filter out self from convo users
      const users = (convo.users ?? []).filter(id => id !== currentUser()?.id)
      let offline = []
      users.forEach(id => {
        if (presence[id]) {
          const p = presence[id]
          if (p === 'offline') {
            offline.push(id)
          }
        } else {
          offline.push(id)
        }
      })

      // get names after checking presence
      let names = []
      // get names here
      offline.forEach(id => {
        if (allUsers[id]) {
          names.push(allUsers[id]?.name)
        }
      })

      // passing in usernames to msg
      if (names.length) {
        setNames(names)
      } else {
        // resetting banner
        setNames('')
      }
    }
  }, [convo, isDirect, currentUser, presence, allUsers])

  // Resets the new status after a scroll
  React.useEffect(() => {
    if (scrolled) {
      handleConvoSelect(session, () => {
        setScrolled(false)
      })
    }
  }, [scrolled, handleConvoSelect, api, session, isDirect, setScrolled])

  const message =
    names?.length === 1
      ? 'is currently offline'
      : names?.length > 1
      ? 'are currently offline'
      : ''

  const handleCommentSubmit = React.useCallback(
    (...args) => {
      onCommentSubmit && onCommentSubmit(...args)
      scrollToBottom()
    },
    [onCommentSubmit, scrollToBottom]
  )

  const handleMessageSubmit = React.useCallback(
    (...args) => {
      onMessageSubmit(...args)
      scrollToBottom()
    },
    [onMessageSubmit, scrollToBottom]
  )

  const handleOnScrollDownClick = React.useCallback(() => {
    quickScrollToBottom()
  }, [quickScrollToBottom])

  if (!Boolean(Object.keys(convo).length)) {
    return null
  }

  return (
    <div className={clsx(classes.root, isDirect && classes.centeredRoot)}>
      <Header
        closeSession={closeSession}
        searchState={{ search, setSearch }}
        isDirect={isDirect}
        isClosed={convo.closed}
        users={convo.users}
        convo={convo}
        filterState={[filter, setFilter]}
        setShowQueue={setShowQueue}
        onMobile={onMobile}
        showBack={isDirect && onMobile}
        viewing={viewing}
        session={session}
      />

      {names?.length !== 0 && verified && (
        <OfflineAlertBanner
          names={names}
          message={message}
          className={classes.banner}
        />
      )}

      <div className={classes.contentArea} ref={chatContentAreaRef}>
        <div ref={messageAreaRef} className={classes.chat} id='chat'>
          {!(convo?.fetched && loaded.includes(session)) &&
          !accepted &&
          !convo?.fetched ? (
            <LoadingSpinner />
          ) : (
            <>
              <Spacer />
              <Timeline
                search={search}
                session={session}
                targetID={targetID}
                targetRef={targetRef}
                isDirect={isDirect}
                isClosed={convo.closed}
                containerRef={messageAreaRef}
                filterNotes={filter === 'notes'}
              />
              <AnimatePresence>
                {!isNearlyAtBottom && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className={classes.fabRoot}
                  >
                    <IconButton onClick={handleOnScrollDownClick}>
                      <Badge
                        color='primary'
                        invisible={hasNoNewUnread}
                        badgeContent='New!'
                      >
                        <ArrowCircleDown
                          color={theme.palette.primary.main}
                          variant='Bulk'
                        />
                      </Badge>
                    </IconButton>
                  </motion.div>
                )}
              </AnimatePresence>

              {!convo.closed && (
                <div
                  className={clsx(
                    classes.typing,
                    !isDirect && classes.typingFull
                  )}
                  style={isSmall ? { width: '100%' } : {}}
                >
                  {typingNames.length > 0 ? (
                    <TypingIndicator
                      names={typingNames}
                      numUsers={users.length}
                      onChat
                    />
                  ) : (
                    <>&zwnj;</>
                  )}
                </div>
              )}

              <div ref={messageEndRef} />
            </>
          )}
        </div>

        <CanSendProvider>
          <Controls
            convo={convo}
            onTypeSet={onTypeSet}
            isDirect={isDirect}
            canMention={canMention}
            agents={agents}
            onMessageSubmit={handleMessageSubmit}
            onCommentSubmit={handleCommentSubmit}
            session={session}
            onTyping={onTyping}
            scrollToBottom={scrollToBottom}
            startRecording={startRecording}
            stopRecording={stopRecording}
            recordingBlob={recordingBlob}
            isRecording={isRecording}
            recordingTime={recordingTime}
            clearRecord={clearRecord}
          />
        </CanSendProvider>
      </div>
    </div>
  )
}

export default ChatWindow
